<script setup lang="ts">
    import { CardTitle } from '@/components/ui/card';
    import { columns } from '@/tables/columns/holiday-pay-obligations';
    import { computed } from 'vue';
    import { Head } from '@inertiajs/vue3';
    import { usePage } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import DataTable from '@/components/datatable/DataTable.vue';
    import PageCard from '@/components/PageCard.vue';
    import Permission from '@/enums/Permission';
    import QueryKey from '@/enums/QueryKey';
    import type { BreadcrumbItem } from '@/types';
    import type { SharedData } from '@/types';

    const { hasPermission } = usePermissions();
    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'dashboards.payroll',
        },
        {
            title: 'holiday-pay-obligations.page_header',
        },
    ];

    const { isPending, isFetching, isError, data, error, refetch } = useQuery({
        queryKey: [QueryKey.HOLIDAY_PAY_OBLIGATIONS, selectedCompanyId],
        queryFn: getHolidayPayObligations,
    });

    async function getHolidayPayObligations() {
        const { data } = await axios.get(
            route('v2.salary.holiday-pay-obligations.table', {
                companyId: selectedCompanyId.value,
            }),
        );
        return data.data;
    }
</script>

<template>
    <Head :title="$t('holiday-pay-obligations.page_header')" />

    <AppLayout
        :breadcrumbs="breadcrumbs"
        require-selected-company
    >
        <PageCard>
            <DataTable
                :table-key="QueryKey.HOLIDAY_PAY_OBLIGATIONS"
                :columns="columns"
                :data="data"
                :loading="isPending"
                :default-column-visibility="{
                    id: false,
                    file_upload: hasPermission(Permission.HOLIDAY_PAY_OBLIGATIONS_UPDATE),
                }"
                :default-sorting="[
                    {
                        id: 'year',
                        desc: true,
                    },
                    {
                        id: 'month',
                        desc: true,
                    },
                ]"
            >
                <template #title>
                    <CardTitle>
                        {{ $t('holiday-pay-obligations.page_header') }}
                    </CardTitle>
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
